import { toast } from "@loot-os/ui/components/sonner";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { BoxEditorLayout } from "../components/BoxEditorLayout";
import { BoxTemplateEnablements } from "../components/BoxTemplateEnablements";
import {
  type BoxDuplicateResult,
  type BoxMetaPatch,
  type BoxMetaResult,
  type BoxRefreshInput,
  boxArtUpload,
  boxWrite,
  type PublishVersionInput,
  type PublishVersionResult,
  refreshResultMessage,
} from "../lib/boxEditor";
import { useTRPC } from "../lib/trpc";

/**
 * One box template, in the same editor a tenant's own box gets. Every write goes
 * through `platform.boxTemplates.*`, never `boxes.*`: this page has no tenant, so an
 * env-scoped procedure would not fail here — it would resolve against whichever
 * tenant the session last held. Using a separate router also means a separate
 * query-key namespace, so a tenant's cached box can never be rendered here.
 *
 * What a box template adds to the shared layout is all blast radius: an archive
 * confirmation, a publish warning, and the Tenants tab — one box, many
 * tenants' storefronts. Amounts in this editor read as dollars (`useCurrency()` returns USD
 * outside a tenant): a box template is priced in cents for every tenant, and each storefront
 * renders it in its own currency.
 */
export function BoxTemplatePage() {
  const { boxId } = useParams({ strict: false }) as { boxId: string };
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const box = useQuery(trpc.platform.boxTemplates.get.queryOptions({ boxId }));

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.platform.boxTemplates.get.queryOptions({ boxId }).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: trpc.platform.boxTemplates.list.pathKey() });
  };

  const templateOpenerStyles = useQuery(trpc.platform.openerStyles.list.queryOptions());
  const updateMutation = useMutation(
    trpc.platform.boxTemplates.update.mutationOptions({ onSuccess: invalidate }),
  );
  const publishMutation = useMutation(
    trpc.platform.boxTemplates.publishVersion.mutationOptions({ onSuccess: invalidate }),
  );
  const refreshMutation = useMutation(
    trpc.platform.boxTemplates.refreshBox.mutationOptions({
      onSuccess: (result) => {
        toast.success(refreshResultMessage(result));
        invalidate();
      },
    }),
  );
  const duplicateMutation = useMutation(
    trpc.platform.boxTemplates.duplicate.mutationOptions({
      onSuccess: (copy) => {
        invalidate();
        toast.success(`Copied as "${copy.name}"`);
        void navigate({ to: "/templates/$boxId", params: { boxId: copy.id } });
      },
    }),
  );
  const startArtUpload = useMutation(trpc.platform.boxTemplates.startArtUpload.mutationOptions());
  const finishArtUpload = useMutation(trpc.platform.boxTemplates.finishArtUpload.mutationOptions());
  const deleteMutation = useMutation(
    trpc.platform.boxTemplates.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.boxTemplates.list.pathKey(),
        });
        void navigate({ to: "/templates" });
      },
    }),
  );

  const enabled = box.data?.enabledCount ?? 0;
  const name = box.data?.name ?? "";

  return (
    <BoxEditorLayout
      box={box.data}
      isLoading={box.isLoading}
      loadError={box.error?.message ?? null}
      writes={{
        update: boxWrite<BoxMetaPatch, BoxMetaResult>(boxId, updateMutation),
        publish: boxWrite<PublishVersionInput, PublishVersionResult>(boxId, publishMutation),
        refresh: boxWrite<BoxRefreshInput>(boxId, refreshMutation),
        duplicate: boxWrite<BoxRefreshInput, BoxDuplicateResult>(boxId, duplicateMutation),
        remove: boxWrite<BoxRefreshInput>(boxId, deleteMutation),
        uploadArt: boxArtUpload(boxId, startArtUpload, finishArtUpload),
      }}
      isCatalog
      openerStyles={
        templateOpenerStyles.data && {
          styles: templateOpenerStyles.data.styles,
          inheritLabel: "Each environment's default",
          inheritedStyleId: null,
        }
      }
      // A top-level section has no TenantLayout header, so it owns its own trail.
      parentCrumb={
        <Link to="/templates" className="text-muted-foreground hover:underline">
          Box templates
        </Link>
      }
      archiveConfirm={
        enabled > 0
          ? {
              title: `Archive "${name}"?`,
              description:
                `${formatNumber(enabled)} ${enabled === 1 ? "environment has" : "environments have"} ` +
                "this box enabled. Archiving takes it off every one of their storefronts " +
                "immediately and blocks opens. Their switches stay on, so activating it again " +
                "restores exactly the same tenants.",
            }
          : undefined
      }
      notice={
        enabled > 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Enabled by {formatNumber(enabled)} {enabled === 1 ? "environment" : "environments"} —
            publishing a version changes this box's contents and price for all of them as soon as it
            lands. See Tenants for which.
          </p>
        ) : undefined
      }
      tabs={[
        {
          value: "tenants",
          label: "Tenants",
          // Rendered only while the tab is open (Radix unmounts the rest), so
          // the enablement query does not fire on a page opened to edit odds.
          content: <BoxTemplateEnablements boxId={boxId} active={box.data?.status === "active"} />,
        },
      ]}
    />
  );
}
